import { z } from 'zod';

/** Server limit for any `extValue` object (media, layout, section, item, customer). */
export const MAX_EXT_VALUE_BYTES = 16 * 1024;

/**
 * JSON-as-text validator shared by forms that edit an `extValue` object. Empty
 * input means "no extra data"; otherwise the text must parse to a JSON object
 * within the byte limit.
 */
export const extValueStringSchema = z
	.string()
	.trim()
	.superRefine((value, ctx) => {
		if (value === '') return;

		let parsed: unknown;
		try {
			parsed = JSON.parse(value);
		} catch {
			ctx.addIssue({ code: 'custom', message: 'Must be valid JSON.' });
			return;
		}

		if (
			typeof parsed !== 'object' ||
			parsed === null ||
			Array.isArray(parsed)
		) {
			ctx.addIssue({ code: 'custom', message: 'Must be a JSON object.' });
			return;
		}

		if (new TextEncoder().encode(value).length > MAX_EXT_VALUE_BYTES) {
			ctx.addIssue({
				code: 'custom',
				message: 'Extra data must be at most 16 KiB.',
			});
		}
	});

/** Parse the form's text into the object sent to the API. */
export function parseExtValue(value: string): Record<string, unknown> {
	const trimmed = value.trim();
	if (trimmed === '') return {};
	return JSON.parse(trimmed) as Record<string, unknown>;
}

/** Seed the form's text from an API object (empty string when empty). */
export function stringifyExtValue(value: Record<string, unknown>): string {
	return Object.keys(value).length === 0 ? '' : JSON.stringify(value, null, 2);
}
